import { useEffect, useState } from 'react';
import { Megaphone, Clock, CheckCircle, Loader2, Calendar, MapPin, Download, Star, Hourglass } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';

// Open Calls (convocatorias) inside the DJ Portal.
// canApply: user has an APPROVED dj profile. agencyDjs: approved managed DJs (for agencies).
export default function OpenCallsSection({ canApply = false, isAgency = false, agencyDjs = [], ownProfileId = null }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyCall, setApplyCall] = useState(null);
  const [message, setMessage] = useState('');
  const [selectedDj, setSelectedDj] = useState('own');
  const [submitting, setSubmitting] = useState(false);
  const [myApps, setMyApps] = useState([]);
  const [selectedSlots, setSelectedSlots] = useState([]);

  const slotText = (s) => `${[s.start_time, s.end_time].filter(Boolean).join('–')}${s.label ? ` · ${s.label}` : ''}`;

  const fetchCalls = () => {
    api.get('/dj/open-calls')
      .then((res) => setCalls(res.data?.open_calls || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const fetchMyApps = () => {
    api.get('/dj/my-open-call-applications')
      .then((res) => setMyApps(res.data?.applications || []))
      .catch(() => {});
  };

  useEffect(() => { fetchCalls(); fetchMyApps(); }, []);

  if (loading || (calls.length === 0 && myApps.length === 0)) return null;

  const handleApply = async () => {
    setSubmitting(true);
    try {
      const payload = { message: message.trim() || null };
      if (isAgency && selectedDj !== 'own') payload.dj_profile_id = selectedDj;
      if (selectedSlots.length > 0) payload.slot_ids = selectedSlots;
      const res = await api.post(`/dj/open-calls/${applyCall.id}/apply`, payload);
      toast.success(res.data?.message || '¡Postulación enviada!');
      setApplyCall(null);
      setMessage('');
      setSelectedDj('own');
      setSelectedSlots([]);
      fetchCalls();
      fetchMyApps();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al postularte');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mb-6 space-y-4" data-testid="open-calls-section">
      {calls.map((call) => {
        const deadline = call.deadline ? new Date(call.deadline) : null;
        return (
          <div
            key={call.id}
            className="relative overflow-hidden rounded-2xl border border-[#D4AF37]/40 bg-gradient-to-r from-[#D4AF37]/15 via-[#D4AF37]/5 to-transparent p-5 sm:p-6"
            data-testid={`open-call-card-${call.id}`}
          >
            <div className="flex flex-col sm:flex-row sm:items-start gap-4">
              {call.event?.image_url && (
                <img src={call.event.image_url} alt="" className="w-full sm:w-28 h-32 sm:h-28 rounded-xl object-cover flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#D4AF37] mb-1 flex items-center gap-1.5">
                  <Megaphone size={12} /> Open Call DJs
                </p>
                <h3 className="font-black text-xl text-white">{call.title}</h3>
                <p className="text-sm text-white/60 mt-1 whitespace-pre-line">{call.description}</p>
                <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-white/50">
                  {call.event && (
                    <span className="flex items-center gap-1"><Calendar size={12} /> {call.event.title}</span>
                  )}
                  {call.event?.venue_name && (
                    <span className="flex items-center gap-1"><MapPin size={12} /> {call.event.venue_name}</span>
                  )}
                  {deadline && (
                    <span className="flex items-center gap-1 text-amber-300">
                      <Clock size={12} /> Cierra el {deadline.toLocaleDateString('es-AR', { day: 'numeric', month: 'long' })}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex-shrink-0">
                {call.already_applied ? (
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 text-sm font-bold" data-testid={`open-call-applied-${call.id}`}>
                    <CheckCircle size={16} /> Ya postulado
                  </span>
                ) : canApply ? (
                  <Button
                    onClick={() => setApplyCall(call)}
                    className="bg-[#D4AF37] hover:bg-[#B8962E] text-black font-bold rounded-full px-6"
                    data-testid={`open-call-apply-btn-${call.id}`}
                  >
                    Postularme
                  </Button>
                ) : (
                  <span className="text-xs text-white/40 max-w-[160px] block text-right">
                    Necesitás una cuenta DJ aprobada para postularte
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {myApps.length > 0 && (
        <div data-testid="my-applications-section">
          <p className="text-[10px] font-black uppercase tracking-[0.25em] text-white/40 mb-2 flex items-center gap-1.5">
            <Megaphone size={11} /> Mis postulaciones
          </p>
          <div className="space-y-3">
            {myApps.map((app) => {
              const eventTitle = app.event?.title || app.call_title || 'Convocatoria';
              const dl = (url) => (url && url.includes('/upload/') ? url.replace('/upload/', '/upload/fl_attachment/') : url);
              if (app.status === 'confirmed') {
                return (
                  <div key={app.id} className="relative overflow-hidden rounded-2xl border border-[#D4AF37]/50 bg-gradient-to-br from-[#D4AF37]/15 via-black to-black p-5" data-testid={`my-app-${app.id}`}>
                    <div className="flex flex-col sm:flex-row gap-4 items-start">
                      {app.share_images?.post && (
                        <img src={app.share_images.post} alt="Placa line-up" className="w-full sm:w-36 rounded-xl border border-[#D4AF37]/40 flex-shrink-0" data-testid={`share-preview-${app.id}`} />
                      )}
                      <div className="flex-1 min-w-0">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/50 text-[#D4AF37] text-[10px] font-black uppercase tracking-widest">
                          <Star size={11} /> Participación confirmada
                        </span>
                        <h4 className="font-black text-lg text-white mt-2">¡Sos parte del line-up de {eventTitle}!</h4>
                        {app.share_images ? (
                          <>
                            <p className="text-sm text-white/60 mt-1">Tu placa oficial está lista. Compartila en Instagram y etiquetá a <span className="text-[#D4AF37] font-bold">@bravoproducciones</span>.</p>
                            <div className="flex flex-wrap gap-2 mt-3">
                              {app.share_images.post && (
                                <a href={dl(app.share_images.post)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#D4AF37] text-black text-xs font-black uppercase tracking-wide hover:bg-[#B8962E] transition-colors" data-testid={`download-post-${app.id}`}>
                                  <Download size={13} /> Post 1:1
                                </a>
                              )}
                              {app.share_images.story && (
                                <a href={dl(app.share_images.story)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border-2 border-[#D4AF37] text-[#D4AF37] text-xs font-black uppercase tracking-wide hover:bg-[#D4AF37]/10 transition-colors" data-testid={`download-story-${app.id}`}>
                                  <Download size={13} /> Story 9:16
                                </a>
                              )}
                            </div>
                          </>
                        ) : (
                          <p className="text-sm text-white/60 mt-1">El equipo te contacta con todos los detalles del show.</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <div key={app.id} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3" data-testid={`my-app-${app.id}`}>
                  {app.event?.image_url && (
                    <img src={app.event.image_url} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-white truncate">{app.call_title || eventTitle}</p>
                    <p className="text-xs text-white/40 truncate">{app.dj_name}{app.event?.title ? ` · ${app.event.title}` : ''}</p>
                  </div>
                  {app.status === 'accepted' ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 text-[10px] font-black uppercase tracking-wide flex-shrink-0">
                      <CheckCircle size={11} /> Seleccionado · coordinando condiciones
                    </span>
                  ) : app.status === 'rejected' ? (
                    <span className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/40 text-[10px] font-black uppercase tracking-wide flex-shrink-0">
                      No quedó esta vez
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-yellow-500/15 border border-yellow-500/30 text-yellow-400 text-[10px] font-black uppercase tracking-wide flex-shrink-0">
                      <Hourglass size={11} /> En revisión
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Dialog open={!!applyCall} onOpenChange={(o) => !o && setApplyCall(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">Postularte a: {applyCall?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {isAgency && agencyDjs.length > 0 && (
              <div>
                <Label className="text-zinc-400 text-xs">¿A quién postulás?</Label>
                <Select value={selectedDj} onValueChange={setSelectedDj}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1" data-testid="open-call-dj-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="own">Mi agencia (perfil propio)</SelectItem>
                    {agencyDjs.map((dj) => (
                      <SelectItem key={dj.id} value={dj.id}>{dj.artistic_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {(applyCall?.time_slots || []).length > 0 && (
              <div>
                <Label className="text-zinc-400 text-xs">¿A qué horario te postulás?</Label>
                <div className="space-y-1.5 mt-1.5" data-testid="open-call-slot-picker">
                  <button
                    type="button"
                    onClick={() => setSelectedSlots([])}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border text-sm font-bold transition-colors ${
                      selectedSlots.length === 0
                        ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]'
                        : 'border-zinc-700 bg-zinc-800/60 text-zinc-300 hover:border-zinc-500'
                    }`}
                    data-testid="open-call-slot-all"
                  >
                    Todos los horarios
                    {selectedSlots.length === 0 && <CheckCircle size={15} />}
                  </button>
                  {applyCall.time_slots.map((s) => {
                    const active = selectedSlots.includes(s.id);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setSelectedSlots((prev) => active ? prev.filter((x) => x !== s.id) : [...prev, s.id])}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border text-sm font-bold transition-colors ${
                          active
                            ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]'
                            : 'border-zinc-700 bg-zinc-800/60 text-zinc-300 hover:border-zinc-500'
                        }`}
                        data-testid={`open-call-slot-opt-${s.id}`}
                      >
                        <span className="font-mono">{slotText(s)}</span>
                        {active && <CheckCircle size={15} />}
                      </button>
                    );
                  })}
                  <p className="text-[11px] text-zinc-500">Podés elegir uno, varios o todos.</p>
                </div>
              </div>
            )}
            <div>
              <Label className="text-zinc-400 text-xs">Mensaje para el equipo de Bravo (opcional)</Label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Contanos por qué sos ideal para este warm up, links a sets, etc."
                rows={4}
                maxLength={1000}
                className="bg-zinc-800 border-zinc-700 mt-1"
                data-testid="open-call-message"
              />
            </div>
            <p className="text-[11px] text-zinc-500">Tu postulación incluye automáticamente tu Press Kit completo.</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setApplyCall(null)}>Cancelar</Button>
            <Button
              onClick={handleApply}
              disabled={submitting}
              className="bg-[#D4AF37] hover:bg-[#B8962E] text-black font-bold"
              data-testid="open-call-submit"
            >
              {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
              Enviar postulación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
